import { useEffect, useMemo, useRef, useState } from 'react'
import { Emma } from '@/components/emma/Emma'
import { useT } from '@/lib/i18n'
import {
  STARTER, CORE_BY_ID, TAKE_INTRO, INTERVIEW_INTRO, FINISH_LATER_AFTER, ACKS,
  fillQuestion, factCard, ringItems,
} from './questionBank'
import { useSpokenPrompts, useDictation } from './speech'
import {
  useVoiceInterview, useSaveInterview, useBuildVoiceProfile,
  type InterviewAnswer, type ProfileResult,
} from './useVoiceSeeding'
import { VoiceRingArc } from '@/components/voice/VoiceRingArc'
import { useEffectiveClient } from '@/hooks/useAuth'
import '@/styles/voice.css'

const POSITION_LABEL = { newcomer: 'Newcomer', reporter: 'Reporter', insider: 'Insider' } as const
const MIN_FOR_PROFILE = 3

/**
 * The voice seeding in onboarding (Brian, Oct 31, voice seeding v2 §12b): the
 * starter five from the v2 bank, the floor, the day that went sideways, then
 * three takes (the opener, the ceiling, two endings). "Finish the rest later"
 * appears after three. Each question is read aloud; answers are dictated or
 * typed and saved as they go, so a closed tab picks up where it stopped. At the
 * end the answers become the voice profile, and the rest of the bank waits in
 * the ring on Emma ("Voice profile started: 13 of 18 left").
 *
 * Props are unchanged from v1. `topics` now only sizes the ring (a take per
 * topic waits there); `restart` starts again at the first of the five with the
 * answers already given ready to edit.
 */
export function VoiceInterview({ topics, onBack, onFinish, finishing, restart = false, doneLabel = 'Start reading headlines' }: {
  topics: string[]
  restart?: boolean
  doneLabel?: string
  onBack: () => void
  /** Called with the profile result, or null when they leave it for later. */
  onFinish: (result: ProfileResult | null) => void
  finishing: boolean
}) {
  const t = useT()
  const client = useEffectiveClient()
  const { data: saved, isLoading } = useVoiceInterview()
  const save = useSaveInterview()
  const build = useBuildVoiceProfile()
  const { speak, stop, muted, setMuted, available: canSpeak } = useSpokenPrompts()

  const ids = STARTER as readonly string[]
  const total = ids.length
  const [ready, setReady] = useState(false)
  const [answers, setAnswers] = useState<InterviewAnswer[]>([])
  const [pos, setPos] = useState(0)
  const [draft, setDraft] = useState('')
  const [ack, setAck] = useState('')
  const [result, setResult] = useState<ProfileResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)

  const dictation = useDictation((x) => setDraft((d) => (d.trim() ? `${d.trim()} ${x}` : x)))

  // Load once: resume what was saved.
  useEffect(() => {
    if (isLoading || ready) return
    const prior = saved?.answers ?? []
    const firstOpen = ids.findIndex((id) => !prior.some((a) => a.id === id))
    const start = restart ? 0 : (firstOpen === -1 ? total : firstOpen)
    setAnswers(prior)
    setPos(start)
    setDraft(prior.find((a) => a.id === ids[start])?.a ?? '')
    setReady(true)
    save.mutate({ question_ids: [...ids], position: start, step: 'interview' })
  }, [isLoading, saved, ready]) // eslint-disable-line react-hooks/exhaustive-deps

  const qid = ids[pos]
  const question = qid ? CORE_BY_ID[qid] : null
  const atEnd = ready && pos >= total
  const answered = answers.filter((a) => ids.includes(a.id) && !a.skipped && a.a.trim()).length
  const firstTakeIdx = ids.findIndex((id) => CORE_BY_ID[id]?.take)
  const text = question ? fillQuestion(question.t, client?.city_state) : ''
  const spoken = question ? `${pos === firstTakeIdx ? `${TAKE_INTRO} ` : ''}${text}` : ''

  // The ring in the app: the core ten, a take per topic, the picks.
  const ring = useMemo(() => {
    const items = ringItems(topics)
    const got = new Set(answers.filter((a) => !a.skipped && a.a.trim()).map((a) => a.id))
    const n = items.filter((x) => got.has(x.id)).length
    return { n, total: items.length }
  }, [topics, answers])

  useEffect(() => {
    if (question) speak(spoken)
  }, [qid]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { textRef.current?.focus() }, [qid])

  function go(next: number, entry: InterviewAnswer | null) {
    dictation.stop()
    stop()
    const list = entry ? [...answers.filter((a) => a.id !== entry.id), entry] : answers
    setAnswers(list)
    setPos(next)
    setDraft(list.find((a) => a.id === ids[next])?.a ?? '')
    // Skipped ones wait at the back of the ring's line.
    const skippedIds = list.filter((a) => a.skipped && ids.includes(a.id)).map((a) => a.id)
    const ringState = saved?.ring ?? {}
    const skipped = [...(ringState.skipped ?? []).filter((x) => !ids.includes(x) || skippedIds.includes(x)),
      ...skippedIds.filter((x) => !(ringState.skipped ?? []).includes(x))]
    save.mutate({ answers: list, position: next, ring: { ...ringState, skipped } })
  }

  function next() {
    if (!question) return
    const v = draft.trim()
    if (!v) return
    setAck(ACKS[pos % ACKS.length])
    window.setTimeout(() => setAck(''), 900)
    go(pos + 1, { id: question.id, q: question.t, a: v })
  }
  function skip() {
    if (!question) return
    go(pos + 1, { id: question.id, q: question.t, a: '', skipped: true })
  }
  function back() {
    if (pos === 0) { stop(); onBack(); return }
    const v = draft.trim()
    go(Math.min(pos, total) - 1, question && v ? { id: question.id, q: question.t, a: v } : null)
  }
  function finishLater() {
    const v = draft.trim()
    go(total, question && v ? { id: question.id, q: question.t, a: v } : null)
  }

  async function makeProfile() {
    setError(null)
    try {
      const r = await build.mutateAsync({ applyDefaultPosition: true })
      setResult(r)
      speak(t('iv.readSpoken'))
    } catch (e) {
      setError((e as Error).message)
    }
  }
  // The read: build it as soon as they reach the end.
  const asked = useRef(false)
  useEffect(() => {
    if (!atEnd || result || asked.current || answered < MIN_FOR_PROFILE) return
    asked.current = true
    void makeProfile()
  }, [atEnd, answered]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return <p className="gg-help">{t('iv.loading')}</p>

  const ringBox = (
    <div className="vr-started">
      <VoiceRingArc pct={ring.total ? ring.n / ring.total : 0} />
      <div>
        <b>{t('iv.ringStarted', { left: ring.total - ring.n, total: ring.total })}</b>
        <span>{t('iv.ringBody')}{answered < total ? ` ${t('iv.ringSkipped')}` : ''}</span>
      </div>
    </div>
  )

  return (
    <div className="onb-iv">
      <div className="onb-iv__top" role="status" aria-live="polite">
        <div className="onb-iv__bar" aria-hidden="true">
          {ids.map((id, i) => {
            const a = answers.find((x) => x.id === id)
            return <i key={id} className={a && !a.skipped && a.a.trim() ? 'is-done' : a?.skipped ? 'is-skip' : i === pos ? 'is-cur' : ''} />
          })}
        </div>
        {canSpeak && (
          <button type="button" className="gg-btn gg-btn--glass onb-iv__mute" aria-pressed={muted}
            onClick={() => setMuted(!muted)}>
            {muted ? t('iv.soundOff') : t('iv.soundOn')}
          </button>
        )}
      </div>

      {!atEnd && question && (
        <>
          <div className="onb-guide">
            <div className="onb-guide__emma"><Emma variant="sheet" /></div>
            <div className="onb-guide__bubble">
              <span className="onb-guide__eyebrow">
                {question.take ? `${t('iv.takeEyebrow')} · ` : ''}{t('iv.questionOf', { n: pos + 1, total })}
              </span>
              {pos === 0 && <p className="onb-guide__hint">{INTERVIEW_INTRO}</p>}
              {pos === firstTakeIdx && <p className="onb-guide__hint">{TAKE_INTRO}</p>}
              <p className="onb-guide__q">{text}</p>
              {question.card && <div className="vr-card">{factCard()}</div>}
              {canSpeak && !muted && (
                <button type="button" className="onb-guide__again" onClick={() => speak(spoken)}>{t('iv.sayAgain')}</button>
              )}
            </div>
          </div>

          <div className="onb-iv__answer">
            <textarea ref={textRef} className="gg-input onb-iv__text" rows={4} value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) next() }}
              placeholder={t('iv.placeholder')}
              aria-label={t('voice.yourAnswer')} />
            {dictation.available && (
              <button type="button" className={`gg-btn gg-btn--glass onb-iv__mic${dictation.listening ? ' is-on' : ''}`}
                onClick={() => (dictation.listening ? dictation.stop() : dictation.start())} aria-pressed={dictation.listening}>
                {dictation.listening ? t('iv.stopTalking') : t('iv.sayInstead')}
              </button>
            )}
          </div>
          <div className={`vr-ack${ack ? ' is-in' : ''}`} aria-live="polite">{ack}</div>

          <div className="onb2__actions onb2__actions--sticky">
            <button type="button" className="gg-btn gg-btn--glass" onClick={back}>{t('iv.back')}</button>
            <button type="button" className="gg-btn gg-btn--quiet" onClick={skip}>{t('voice.skipOne')}</button>
            <button type="button" className="gg-btn gg-btn--amber" onClick={next} disabled={!draft.trim()}>{t('voice.next')}</button>
          </div>
          {answered >= FINISH_LATER_AFTER && (
            <button type="button" className="onb-iv__later" onClick={finishLater} disabled={finishing}>
              {t('iv.finishLater')}
            </button>
          )}
        </>
      )}

      {atEnd && !result && (
        <>
          <div className="onb-guide">
            <div className="onb-guide__emma"><Emma variant="sheet" /></div>
            <div className="onb-guide__bubble">
              <span className="onb-guide__eyebrow">{t('iv.readTitle')}</span>
              {answered >= MIN_FOR_PROFILE
                ? <p className="onb-guide__q">{build.isPending ? t('iv.listeningBack', { n: answered }) : t('iv.readLede')}</p>
                : <p className="onb-guide__q">{t('iv.needMore', { min: MIN_FOR_PROFILE })}</p>}
            </div>
          </div>
          {answered < MIN_FOR_PROFILE && ringBox}
          <div className="onb2__actions">
            <button type="button" className="gg-btn gg-btn--glass" onClick={() => go(Math.max(0, total - 1), null)}>{t('iv.back')}</button>
            {answered >= MIN_FOR_PROFILE ? (
              <button type="button" className="gg-btn gg-btn--amber" disabled={build.isPending} onClick={() => void makeProfile()}>
                {build.isPending ? t('iv.writing') : t('iv.makeProfile')}
              </button>
            ) : (
              <button type="button" className="gg-btn gg-btn--amber" onClick={() => onFinish(null)} disabled={finishing}>
                {finishing ? t('iv.finishing') : doneLabel}
              </button>
            )}
          </div>
          {build.isPending && <p className="gg-help">{t('iv.takesMinute')}</p>}
          {error && (
            <>
              <p className="gg-error">{error}</p>
              <button type="button" className="onb-iv__later" onClick={() => onFinish(null)} disabled={finishing}>
                {t('iv.skipSaved')}
              </button>
            </>
          )}
        </>
      )}

      {result && (
        <>
          <div className="onb-profile">
            <span className="onb-guide__eyebrow">{t('iv.readTitle')}</span>
            {result.profile?.rhythm && <p className="onb-profile__line">{result.profile.rhythm}</p>}
            {result.profile?.character && <p className="onb-profile__line">{result.profile.character}</p>}
            {!!result.profile?.phrasesUsed.length && (
              <div className="onb-profile__group">
                <h3>{t('iv.youSay')}</h3>
                <ul>{result.profile.phrasesUsed.map((p) => <li key={p}>{p}</li>)}</ul>
              </div>
            )}
            {!!result.profile?.phrasesNever.length && (
              <div className="onb-profile__group">
                <h3>{t('iv.youNever')}</h3>
                <ul className="is-never">{result.profile.phrasesNever.map((p) => <li key={p}>{p}</li>)}</ul>
              </div>
            )}
            {result.profile?.defaultPosition && (
              <p className="onb-profile__line">{t('iv.defaultPosition', { pos: POSITION_LABEL[result.profile.defaultPosition] })}</p>
            )}
            <p className="onb-profile__line onb-profile__small">{t('iv.seedling')}</p>
            {result.coin && <p className="onb-profile__coin">{t('iv.coin')}</p>}
          </div>
          {ringBox}
          <div className="onb2__actions">
            <button type="button" className="gg-btn gg-btn--amber" onClick={() => onFinish(result)} disabled={finishing}>
              {finishing ? t('iv.finishing') : doneLabel}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
